// 1. Framework / library imports
import { memo, useCallback, useEffect, useState } from "react";

// 2. Helpers & Hooks
import { useTranslate } from "helpers";

// 3. API & Types
import {
    deleteVehiclePictureAPI,
    getFleetVehiclePicturesAPI,
    reorderVehiclePicturesAPI,
} from "services/api/fleet/vehicles/pictures/api";
import { showNotification } from "services/api/snackbar";
import { FleetVehiclePictureRow } from "services/api/fleet/vehicles/pictures/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

// 4. UI Components
import { PictureGalleryPreview } from "ui/components/Pictures";

// 5. Local Types
import { VehiclePicturePreviewProps } from "./types";

const VehiclePictureEditCard: React.FC<VehiclePicturePreviewProps> = (props) => {
    const { show, vehicleId, pictureId, onSuccess, onChange, onClose } = props;

    const { t } = useTranslate();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [vehiclePictures, setVehiclePictures] = useState<
        DataWithPagination<FleetVehiclePictureRow>
    >(initialDataWithPagination);

    useEffect(() => {
        if (vehicleId) {
            fetchVehiclePictures(vehiclePictures.pagination, vehicleId);
        }
    }, [vehicleId]);

    const fetchVehiclePictures = async (
        pagination: Pagination,
        vehicleId: string
    ) => {
        setIsLoading(true);
        try {
            const results = await getFleetVehiclePicturesAPI(
                vehicleId,
                pagination,
                ""
            );
            setVehiclePictures(results);
        } catch {
            setVehiclePictures(initialDataWithPagination);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = useCallback(async (ids: string[]) => {
        setIsLoading(true);
        try {
            await Promise.all(
                ids.map((id) => deleteVehiclePictureAPI(id, t))
            );
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    const handleReorder = useCallback(async (orderedIds: string[]) => {
        await reorderVehiclePicturesAPI(vehicleId, orderedIds, t, true);
        showNotification(t("admin.fleet.vehicle.picture.reorder.message.success"), {
            variant: "success",
        });
        await fetchVehiclePictures(vehiclePictures.pagination, vehicleId);
        if (onChange) onChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleId, t, onChange]);

    const handleSave = useCallback(() => {
        if (onSuccess) {
            onSuccess();
        }
    }, [onSuccess]);

    return (
        <PictureGalleryPreview
            show={show}
            pictures={vehiclePictures.data}
            initialSelectedId={pictureId}
            isLoading={isLoading}
            allowDelete={true}
            onDelete={handleDelete}
            allowReorder={true}
            onReorder={handleReorder}
            onClose={onClose}
            onSave={handleSave}
            deleteDialogTitle={t("admin.fleet.vehicle.edit.picture.dialog.delete.title")}
            deleteDialogMessage={t("admin.fleet.vehicle.edit.picture.dialog.delete.message")}
            deleteDialogConfirmText={t("admin.fleet.vehicle.edit.picture.dialog.delete.confirmtext")}
            deleteDialogCancelText={t("admin.fleet.vehicle.edit.picture.dialog.delete.canceltext")}
        />
    );
};

export default memo(VehiclePictureEditCard);
